// =============================================================================
// SPARRING AI SERVICE - OpenAI GPT Integration for Sparring Chat
// =============================================================================
//
// Uses OpenAI GPT-4 for high-quality coaching conversations
// Falls back to Gemini if OpenAI is not available
//
// =============================================================================

import { SparringMessage, SparringMode } from '../../types';
import { 
  getStructuredPrompt, 
  getFreePrompt,
  getReflectionPrompt,
  getProjectPrompt,
  calculateCurrentStep,
  calculateCurrentProjectStep,
  GoalContext,
  SparringPrompt
} from './sparringEngine';
import { openAI } from '../../../../../services/openAIService';

// Conditional import for Google AI SDK (same as other services)
let GoogleGenerativeAI: any = null;
try {
  const googleAI = require('@google/generative-ai');
  GoogleGenerativeAI = googleAI.GoogleGenerativeAI;
} catch (error) {
  console.warn('Google AI package not installed, Gemini fallback will be disabled');
}

// -----------------------------------------------------------------------------
// TYPES
// -----------------------------------------------------------------------------

export interface SparringAIResponse {
  content: string;
  success: boolean;
  error?: string;
}

// -----------------------------------------------------------------------------
// SERVICE CLASS
// -----------------------------------------------------------------------------

class SparringAIService {
  private openAIKey: string | null = null;
  private geminiKey: string | null = null;
  private geminiModel: any = null;

  constructor() {
    this.openAIKey = process.env.REACT_APP_OPENAI_API_KEY || null;
    this.geminiKey = process.env.REACT_APP_GOOGLE_AI_API_KEY || null;
    
    // Initialize Gemini SDK (same way as AI overlay and other services)
    if (GoogleGenerativeAI && this.geminiKey) {
      try {
        const genAI = new GoogleGenerativeAI(this.geminiKey);
        // Use gemini-2.5-flash (nieuwste, snelste model - feb 2026)
        this.geminiModel = genAI.getGenerativeModel({ model: 'gemini-2.5-flash' });
        console.log('✅ Gemini SDK initialized for sparring with gemini-2.5-flash');
      } catch (error) {
        console.error('Failed to initialize Gemini SDK:', error);
      }
    }
  }

  /**
   * Check if AI is available
   */
  isAvailable(): boolean {
    return !!(this.openAIKey || this.geminiKey);
  }

  /**
   * Get which AI provider is being used
   */
  getProvider(): 'openai' | 'gemini' | 'none' {
    if (this.openAIKey) return 'openai';
    if (this.geminiKey) return 'gemini';
    return 'none';
  }

  /**
   * Generate AI response for sparring session
   */
  async generateResponse(
    goalContext: GoalContext,
    mode: SparringMode,
    messages: SparringMessage[]
  ): Promise<SparringAIResponse> {
    // Calculate current step for structured/project mode
    let currentStep = 0;
    if (mode === 'structured') {
      currentStep = calculateCurrentStep(messages);
    } else if (mode === 'project') {
      currentStep = calculateCurrentProjectStep(messages);
    }
    
    // Generate the appropriate prompt based on mode
    let prompt: SparringPrompt;
    if (mode === 'structured') {
      prompt = getStructuredPrompt(goalContext, currentStep, messages);
    } else if (mode === 'project') {
      prompt = getProjectPrompt(goalContext, currentStep, messages);
    } else if (mode === 'reflection') {
      prompt = getReflectionPrompt(goalContext, messages);
    } else {
      prompt = getFreePrompt(goalContext, messages);
    }

    // Try OpenAI first (same as AI overlay), then fall back to Gemini only if OpenAI fails
    let response: SparringAIResponse;
    
    // Check if OpenAI is configured (same check as AI overlay)
    if (openAI.isConfigured()) {
      console.log('🤖 Using OpenAI for sparring (same as AI overlay)');
      response = await this.callOpenAI(prompt, messages);
      
      // If OpenAI fails, try Gemini as fallback (but log the OpenAI error)
      if (!response.success) {
        console.error('❌ OpenAI failed:', response.error);
        if (this.geminiKey) {
          console.log('⚠️ Falling back to Gemini...');
          const geminiResponse = await this.callGemini(prompt, messages);
          // If Gemini also fails, return OpenAI error (more helpful)
          if (!geminiResponse.success) {
            return {
              content: '',
              success: false,
              error: `OpenAI fout: ${response.error}. Gemini fallback ook gefaald: ${geminiResponse.error}. Controleer je API keys.`
            };
          }
          return geminiResponse;
        } else {
          // No Gemini fallback available
          return response;
        }
      }
    } else if (this.geminiKey) {
      console.log('⚠️ OpenAI not configured, using Gemini as fallback');
      response = await this.callGemini(prompt, messages);
    } else {
      return {
        content: '',
        success: false,
        error: 'Geen AI provider beschikbaar. Configureer REACT_APP_OPENAI_API_KEY (aanbevolen, zoals AI overlay) of REACT_APP_GOOGLE_AI_API_KEY.'
      };
    }

    // Post-processing for step 7: Remove any questions the AI might have added
    if (response.success && (mode === 'structured' || mode === 'project') && currentStep === 7) {
      response.content = this.sanitizeStep7Response(response.content);
    }

    return response;
  }

  /**
   * Remove questions from step 7 responses
   * The AI sometimes ignores instructions and adds questions anyway
   */
  private sanitizeStep7Response(content: string): string {
    // Split into sentences
    const sentences = content.split(/(?<=[.!?])\s+/);
    
    // Filter out sentences that are questions
    const filteredSentences = sentences.filter(sentence => {
      const trimmed = sentence.trim();
      // Remove sentences ending with ?
      if (trimmed.endsWith('?')) return false;
      // Remove sentences starting with question words
      const questionStarters = /^(wat|hoe|wanneer|wie|waarom|welke|kun je|zou je|wil je|klopt|is dat|vind je)/i;
      if (questionStarters.test(trimmed)) return false;
      return true;
    });

    // If we filtered out too much, return original but replace ? with .
    if (filteredSentences.length < 2) {
      return content.replace(/\?/g, '.');
    }

    return filteredSentences.join(' ');
  }

  /**
   * Call OpenAI GPT-4 API using OpenAI SDK directly (for multi-turn conversations)
   */
  private async callOpenAI(
    prompt: SparringPrompt,
    messages: SparringMessage[]
  ): Promise<SparringAIResponse> {
    try {
      // Check if OpenAI is configured (same check as AI overlay)
      if (!openAI.isConfigured()) {
        throw new Error('OpenAI API key niet geconfigureerd');
      }

      // Build conversation messages in OpenAI format (proper multi-turn conversation)
      const conversationMessages: Array<{role: 'system' | 'user' | 'assistant', content: string}> = [
        { role: 'system', content: prompt.systemPrompt }
      ];

      // Add conversation history
      for (const msg of messages) {
        conversationMessages.push({
          role: msg.role === 'user' ? 'user' : 'assistant',
          content: msg.content
        });
      }

      // If conversation just started, add instruction to begin
      if (messages.length === 0) {
        conversationMessages.push({
          role: 'user',
          content: 'Start het gesprek met een open vraag over mijn doel.'
        });
      }

      // Access the internal OpenAI client from openAIService
      // @ts-ignore - accessing private property for multi-turn conversation support
      const openaiClient = openAI['openai'];
      
      if (!openaiClient) {
        throw new Error('OpenAI client niet beschikbaar');
      }

      const response = await openaiClient.chat.completions.create({
        model: 'gpt-4o-mini', // Same as AI overlay uses for quick responses
        messages: conversationMessages,
        max_tokens: 300,
        temperature: 0.7,
      });

      const content = response.choices[0]?.message?.content?.trim();

      if (!content || content.length === 0) {
        throw new Error('Geen antwoord ontvangen van AI');
      }

      return {
        content,
        success: true
      };
    } catch (error: any) {
      console.error('OpenAI API error:', error);
      
      // Don't fallback here - let the caller handle fallback
      // This allows generateResponse to try Gemini if OpenAI fails
      return {
        content: '',
        success: false,
        error: error.message || 'Fout bij het aanroepen van OpenAI'
      };
    }
  }

  /**
   * List available Gemini models for this API key
   */
  private async listAvailableModels(): Promise<string[]> {
    try {
      if (!this.geminiKey) return [];
      
      // Try both API versions to list models
      const listUrls = [
        `https://generativelanguage.googleapis.com/v1beta/models?key=${this.geminiKey}`,
        `https://generativelanguage.googleapis.com/v1/models?key=${this.geminiKey}`,
      ];
      
      for (const url of listUrls) {
        try {
          const response = await fetch(url);
          if (response.ok) {
            const data = await response.json();
            const models = data.models?.map((m: any) => m.name?.replace('models/', '') || m.name) || [];
            if (models.length > 0) {
              console.log('✅ Available Gemini models:', models);
              return models;
            }
          }
        } catch (e) {
          // Try next URL
        }
      }
      
      return [];
    } catch (error) {
      console.warn('Could not list available models:', error);
      return [];
    }
  }

  /**
   * Call Google Gemini API using SDK (same as AI overlay)
   * Falls back to REST API if SDK not available
   */
  private async callGemini(
    prompt: SparringPrompt,
    messages: SparringMessage[]
  ): Promise<SparringAIResponse> {
    try {
      if (!this.geminiKey) {
        throw new Error('Gemini API key not configured');
      }

      // Build the full prompt for Gemini
      let fullPrompt = prompt.systemPrompt + '\n\n';
      
      // Add conversation history
      if (messages.length > 0) {
        fullPrompt += 'GESPREK TOT NU TOE:\n';
        for (const msg of messages) {
          const role = msg.role === 'user' ? 'Gebruiker' : 'Sparringpartner';
          fullPrompt += `${role}: ${msg.content}\n\n`;
        }
        fullPrompt += '\nGeef nu je volgende reactie als sparringpartner:';
      } else {
        fullPrompt += 'Start het gesprek met een open vraag over het doel van de gebruiker.';
      }

      // Try SDK first (same as AI overlay)
      if (this.geminiModel) {
        try {
          const result = await this.geminiModel.generateContent(fullPrompt);
          const response = await result.response;
          const content = response.text().trim();

          if (content) {
            console.log('✅ Gemini SDK success (same as AI overlay)');
            return {
              content,
              success: true
            };
          }
        } catch (sdkError: any) {
          console.warn('Gemini SDK failed, falling back to REST API:', sdkError.message);
          // Fall through to REST API fallback
        }
      }

      // Fallback to REST API if SDK not available or failed
      const requestBody = {
        contents: [{
          parts: [{ text: fullPrompt }]
        }],
        generationConfig: {
          maxOutputTokens: 300,
          temperature: 0.7,
        }
      };

      // Try REST API with newest Gemini models (feb 2026)
      const restAttempts = [
        { version: 'v1beta', model: 'gemini-2.5-flash' }, // Nieuwste, snelste
        { version: 'v1beta', model: 'gemini-2.0-flash' }, // Fallback
      ];

      for (const attempt of restAttempts) {
        try {
          const url = `https://generativelanguage.googleapis.com/${attempt.version}/models/${attempt.model}:generateContent?key=${this.geminiKey}`;
          
          const response = await fetch(url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestBody)
          });

          if (response.ok) {
            const data = await response.json();
            const content = data.candidates?.[0]?.content?.parts?.[0]?.text?.trim();

            if (content) {
              console.log(`✅ Gemini REST API success with ${attempt.version}/${attempt.model}`);
              return {
                content,
                success: true
              };
            }
          }
        } catch (error: any) {
          console.log(`❌ ${attempt.version}/${attempt.model} failed: ${error.message}`);
        }
      }

      throw new Error('Gemini API niet beschikbaar. Controleer je API key configuratie.');

    } catch (error: any) {
      console.error('Gemini API error:', error);
      return {
        content: '',
        success: false,
        error: error.message || 'Fout bij het aanroepen van Gemini. Controleer je API key configuratie.'
      };
    }
  }
}

// Export singleton instance
export const sparringAIService = new SparringAIService();

// Export types
export type { GoalContext };
